import React, {
  MouseEventHandler,
  ReactElement,
  useEffect,
  useState,
} from 'react';
import { useTranslate } from 'hooks/translate';
import ReactDatePicker from 'react-datepicker';
import nationalities from 'constants/nationalities';
import { DATEPICKER_FORMAT } from 'constants/date-format';
import { enGB } from 'date-fns/locale';

import passengerIcon from '../../../assets/svgs/icon-passenger.svg';
import { useReservations } from 'contexts/reservations-context';
import { passengerDeclarationsDefault } from 'scenes/reservation/guest-information/guest-information';
import Alert from 'components/alerts/alert';
import { parseJsonData } from 'helpers/utils';
import { getH1Style, getInputStyle, getSelectStyle } from './style-utils';

interface PassengerInformationProps {
  back: MouseEventHandler;
  next: () => void;
  user?: Customer;
  hostedCamp?: boolean;
}

export default function PassengerInformation({
  back,
  next,
  user,
  hostedCamp,
}: PassengerInformationProps): ReactElement {
  const { t } = useTranslate();
  const [errorMessage, setErrorMessage] = useState('');

  const {
    numberOfPassengers,
    passengerDeclarations,
    setPassengerDeclarations,
  } = useReservations();

  const onChangePassenger = (
    index: number,
    property: string,
    value: string | number | Date,
    companion?: boolean,
  ): void => {
    setErrorMessage('');
    if (property === 'dateOfBirth') {
      value = new Date(value).toISOString().split('T')[0];
    }
    const passengerDeclarationsData = parseJsonData(
      JSON.stringify(passengerDeclarations),
    );
    if (companion) {
      passengerDeclarationsData[0].passengerCompanion[index][property] = value;
    } else {
      passengerDeclarationsData[index][property] = value;
    }
    setPassengerDeclarations(passengerDeclarationsData);
  };

  const getCompanionDateOfBirth = (i: number): Date | null => {
    if (passengerDeclarations[0].passengerCompanion[i]?.dateOfBirth) {
      return new Date(
        passengerDeclarations[0].passengerCompanion[i].dateOfBirth ?? '',
      );
    }
    return null;
  };

  const validate = (): boolean => {
    if (
      !passengerDeclarations[0].dateOfBirth ||
      !passengerDeclarations[0].nationality
    ) {
      return false;
    }
    for (const company of passengerDeclarations[0].passengerCompanion) {
      const isPopulated = !(
        company.dateOfBirth === '' &&
        company.firstName === '' &&
        company.lastName === ''
      );
      if (
        isPopulated &&
        (!company.dateOfBirth || !company.firstName || !company.lastName)
      ) {
        return false;
      }
    }
    return true;
  };

  const submit = (): void => {
    setErrorMessage('');
    if (validate()) {
      next();
    } else {
      setErrorMessage(t('reservation.guestInformation.completeData'));
    }
  };

  useEffect(() => {
    if (!passengerDeclarations[0].firstName)
      setPassengerDeclarations([
        {
          ...passengerDeclarationsDefault,
          firstName: user?.firstName ?? '',
          lastName: user?.lastName ?? '',
          nationality: user?.country.toUpperCase() ?? '',
          passengerCompanion: [...new Array(numberOfPassengers - 1)].map(
            (e, i) => {
              return {
                firstName: '',
                lastName: '',
                dateOfBirth: '',
              };
            },
          ),
        },
      ]);
  }, [
    numberOfPassengers,
    setPassengerDeclarations,
    user,
    passengerDeclarations,
  ]);
  const styleOverrideH1 = getH1Style(hostedCamp);
  const styleOverrideInput = getInputStyle(hostedCamp);
  const styleOverrideSelect = getSelectStyle(hostedCamp);
  return (
    <div className='booking-content booking-passenger-information'>
      <h1 style={styleOverrideH1}>
        {' '}
        {t('reservation.guestInformation.campPassengerHeader')}
      </h1>
      <p className='booking-subtitle'>
        {t('reservation.guestInformation.campPassengerSubHeader')}
      </p>
      <div className='booking-forms'>
        <div className='row'>
          <div className='col passenger-details'>
            <img src={passengerIcon} alt='Passenger' />
            <div className='main-passenger'>
              <p>
                {t('reservation.guestInformation.passenger')}:
                <strong>
                  {` ${passengerDeclarations[0].firstName} ${passengerDeclarations[0].lastName}`}
                  <span className='edit-main-passenger' onClick={back}>
                    &#9679;&#9679;&#9679;
                  </span>
                </strong>
              </p>
            </div>
          </div>
        </div>
        <div className='row'>
          <div className='col'>
            <label>{t('reservation.guestInformation.dateOfBirth')}</label>
            <ReactDatePicker
              name='dob'
              dateFormat={DATEPICKER_FORMAT}
              placeholderText={DATEPICKER_FORMAT}
              locale={enGB}
              onChange={(date: Date) => {
                onChangePassenger(0, 'dateOfBirth', date);
              }}
              selected={
                passengerDeclarations[0].dateOfBirth
                  ? new Date(passengerDeclarations[0].dateOfBirth)
                  : null
              }
              scrollableYearDropdown
              yearDropdownItemNumber={80}
              showYearDropdown
              showMonthDropdown
              maxDate={new Date()}
            />
          </div>
        </div>
        <div className='row'>
          <div className='col'>
            <label>{t('reservation.guestInformation.nationality')}</label>
            <div className='select-container'>
              <select
                style={styleOverrideSelect}
                value={passengerDeclarations[0].nationality}
                onChange={(e) => {
                  onChangePassenger(0, 'nationality', e.target.value);
                }}
              >
                {nationalities.map((nationality, index) => (
                  <option key={index} value={nationality.alpha_2_code}>
                    {nationality.nationality}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        {numberOfPassengers &&
          [...new Array(numberOfPassengers - 1)].map((x, i) => (
            <div key={i}>
              <div className='row empty' />
              <div className='row'>
                <div className='col passenger-details'>
                  <img src={passengerIcon} alt='Passenger' />
                  <div className='main-passenger'>
                    <p>
                      {t('reservation.guestInformation.passenger')} {i + 2}
                    </p>
                  </div>
                </div>
              </div>
              <div className='row'>
                <div className='col'>
                  <label>{t('reservation.guestInformation.firstName')}</label>
                  <input
                    style={styleOverrideInput}
                    type='text'
                    onChange={(e) => {
                      onChangePassenger(i, 'firstName', e.target.value, true);
                    }}
                    value={
                      passengerDeclarations[0].passengerCompanion[i]
                        ?.firstName ?? ''
                    }
                    name='firstName'
                  />
                </div>
                <div className='col'>
                  <label>{t('reservation.guestInformation.lastName')}</label>
                  <input
                    style={styleOverrideInput}
                    type='text'
                    onChange={(e) => {
                      onChangePassenger(i, 'lastName', e.target.value, true);
                    }}
                    value={
                      passengerDeclarations[0].passengerCompanion[i]
                        ?.lastName ?? ''
                    }
                    name='lastName'
                  />
                </div>
              </div>
              <div className='row'>
                <div className='col'>
                  <label>{t('reservation.guestInformation.dateOfBirth')}</label>
                  <ReactDatePicker
                    name='companionDob'
                    dateFormat={DATEPICKER_FORMAT}
                    placeholderText={DATEPICKER_FORMAT}
                    locale={enGB}
                    onChange={(date: Date) => {
                      onChangePassenger(i, 'dateOfBirth', date, true);
                    }}
                    selected={getCompanionDateOfBirth(i)}
                    scrollableYearDropdown
                    yearDropdownItemNumber={80}
                    showYearDropdown
                    showMonthDropdown
                    maxDate={new Date()}
                  />
                </div>
              </div>
            </div>
          ))}
        <div className='row empty' />
        {errorMessage && <Alert message={errorMessage} variant='error' />}
        <div className='row empty' />
        <button onClick={submit}>{t('reservation.registration.next')}</button>
      </div>
    </div>
  );
}
